import {
  Badge,
  BadgeProps,
  Button,
  FluentProvider,
  makeStyles,
  mergeClasses,
  tokens,
  webDarkTheme,
  webLightTheme,
} from "@fluentui/react-components";
import {
  Add20Regular,
  History20Regular,
  Settings20Regular,
} from "@fluentui/react-icons";
import {AppProvider, useAppState} from "../context/AppContext";
import {ChatPanel} from "./ChatPanel";
import {Settings} from "./Settings";
import {ThreadList} from "./ThreadList";
import {getRuntimeConfig} from "../../services/runtimeConfig";
import {isOfficeDarkTheme} from "../../services/officeTheme";

/**
 * Deployment environment indicator. Driven by `ADDON_ENV`, injected at runtime
 * by server.js into `window.__RUNTIME_CONFIG__` so one build/image works across
 * staging/stable/prod. Falls back to the build-time value (webpack Dotenv) when
 * running `webpack serve` locally. Unset/unknown → no badge shown.
 */
type Environment = "staging" | "stable" | "prod";

const ENV_BADGES: Record<
  Environment,
  {label: string; color: BadgeProps["color"]}
> = {
  staging: {label: "STAGING", color: "warning"},
  stable: {label: "STABLE", color: "brand"},
  prod: {label: "PROD", color: "success"},
};

function getEnvBadge(): {label: string; color: BadgeProps["color"]} | null {
  const raw = getRuntimeConfig().ADDON_ENV ?? process.env.ADDON_ENV ?? "";
  const env = raw.toLowerCase().trim();
  if (env === "staging" || env === "stable" || env === "prod") {
    return ENV_BADGES[env];
  }
  return null;
}

const envBadge = getEnvBadge();

const useStyles = makeStyles({
  // Absolute positioning against #root (which is height-locked in the HTML)
  // sidesteps the FluentProvider wrapper div not propagating height: 100%.
  root: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // The chat stays mounted behind the overlays: unmounting it would abandon an
  // in-flight turn's Stop button and throw away a half-typed question.
  hidden: {
    display: "none",
  },
  // Fills the outer root; height must come from flex, not another 100% chain.
  chatLayer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  // Office already renders the add-in's display name above the pane, so this
  // bar carries actions only — repeating the title just costs vertical space.
  actionBar: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    padding: "4px 8px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  spacer: {
    flex: 1,
  },
  content: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
});

function AppContent() {
  const styles = useStyles();
  const {view, setView, newChat, apiKey} = useAppState();

  return (
    <div className={styles.root}>
      <div
        className={mergeClasses(
          styles.chatLayer,
          view !== "chat" && styles.hidden,
        )}
      >
        <div className={styles.actionBar}>
          <Button
            appearance="subtle"
            size="small"
            icon={<History20Regular />}
            onClick={() => setView("threads")}
            aria-label="Chats"
            title="Chats"
          >
            Chats
          </Button>
          <Button
            appearance="subtle"
            size="small"
            icon={<Add20Regular />}
            onClick={newChat}
            aria-label="New chat"
            title="New chat"
          >
            New
          </Button>
          <Button
            appearance="subtle"
            size="small"
            icon={<Settings20Regular />}
            onClick={() => setView("settings")}
            aria-label="Settings"
            title="Settings"
          >
            Settings
          </Button>
          <div className={styles.spacer} />
          {envBadge && (
            <Badge appearance="filled" color={envBadge.color} size="small">
              {envBadge.label}
            </Badge>
          )}
        </div>
        <div className={styles.content}>
          <ChatPanel />
        </div>
      </div>

      {view === "settings" && <Settings />}
      {view === "threads" && apiKey && <ThreadList />}
    </div>
  );
}

export function App() {
  return (
    <FluentProvider theme={isOfficeDarkTheme() ? webDarkTheme : webLightTheme}>
      <AppProvider>
        <AppContent />
      </AppProvider>
    </FluentProvider>
  );
}
